/**
 * web/src/lib/exploreGroups.ts — the Explore drawer's rows, derived from each
 * screen's own sub-tab list (card 8hGV3kwDaVoB).
 *
 * Jonathan, email 2026-11-04 17:37: "mapping of explore and life sub tabs
 * don't agree (this needs durable solution across pages)". The two lists were
 * hand-written in different files and had already drifted: Week had no door in
 * Explore at all, and Plan arrived labelled "Weekly sync" and Requests "For
 * review", so the label you left by was not the label you arrived at.
 *
 * This module is the durable half: it takes the three screens' tab arrays and
 * builds the doors from them — key, label and order all read from the one
 * array. Nothing here names a tab. A tab added to LIFE_TABS appears in Explore
 * the same commit; a tab deleted from it takes its door with it.
 *
 * Pure by design (no React, no DOM) so a probe can call it directly.
 */
import type { SubTabDef } from '../components/SubTabs';
import { subPanelId } from '../components/SubTabs';
import { LIFE_TABS } from '../screens/Life/tabs';
import { US_TABS } from '../screens/Us/tabs';
import { NB_TABS } from '../screens/Notebook/tabs';

export type ExploreTab = 'us' | 'life' | 'notebook';

/** What a drawer row asks the shell to do. Structurally a `MorePick`; declared
 *  here so the builder depends on nothing that depends on the drawer. */
export interface ExplorePick {
  tab: ExploreTab;
  /** Land on the tab, then scroll this section into view. */
  scrollTo?: string;
  /** Notebook sub-view: land on that collection tab. */
  collection?: string;
  /** Life / Us sub-tab to open on arrival. */
  lifeSub?: string;
  usSub?: string;
}

export interface ExploreSub {
  /** The name the door shows. The tab's own `exploreLabel`, else its label. */
  label: string;
  /** The tab key the row lands on — the identity a test compares. */
  key: string;
  pick: ExplorePick;
}

export interface ExploreGroup {
  tab: ExploreTab;
  label: string;
  subs: ExploreSub[];
}

/** The three screens' rows, newest export each. Passed in rather than imported
 *  by the builder so a probe can hand it a mutated list and watch the door go. */
export interface ExploreSources {
  us: SubTabDef[];
  life: SubTabDef[];
  notebook: SubTabDef[];
}

export const EXPLORE_SOURCES: ExploreSources = {
  us: US_TABS,
  life: LIFE_TABS,
  notebook: NB_TABS,
};

/**
 * One door per destination tab, not per euphemism. The group heading is the
 * screen's own title, so the drawer reads Us / Life / Notebook exactly as the
 * bottom bar does.
 *
 * Order is the tab array's order — the drawer cannot invent a sequence the
 * screen does not have — and a tab with `explore: false` is skipped (Notebook's
 * Vault, which is reachable inside Notebook and left the drawer in card
 * 3YypYdChpTZD).
 */
export function buildExploreGroups(src: ExploreSources): ExploreGroup[] {
  const rows = (tabs: SubTabDef[], make: (key: string) => ExplorePick): ExploreSub[] =>
    tabs
      .filter(t => t.explore !== false)
      .map(t => ({ key: t.key, label: t.exploreLabel ?? t.label, pick: make(t.key) }));

  return [
    {
      tab: 'us',
      label: 'Us',
      subs: rows(src.us, key => ({
        tab: 'us',
        usSub: key,
        scrollTo: subPanelId('us', key),
      })),
    },
    {
      tab: 'life',
      label: 'Life',
      subs: rows(src.life, key => ({
        tab: 'life',
        lifeSub: key,
        scrollTo: subPanelId('life', key),
      })),
    },
    {
      tab: 'notebook',
      // The collection row is the destination itself: the pick switches the
      // tab and the screen's own row selects the collection.
      label: 'Notebook',
      subs: rows(src.notebook, key => ({ tab: 'notebook', collection: key })),
    },
  ];
}
